import React, { useMemo, useEffect, useState } from "react";
import Table from "../../../Components/Table/index";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import Spinner from "react-bootstrap/Spinner";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import { Link } from "react-router-dom";

export default function BookedSurgeryList() {
  const [surgeryList, setSurgeryList] = useState([]);
  const [allSurgeries, setAllSurgeries] = useState([]); // All fetched records
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pagesFetched, setPagesFetched] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  const fetchData = React.useCallback(
    () => {
      setIsLoading(true);

      let allBookings = [];
      let pageCount = 0;
      let lastKey = null;

      const fetchPage = (cursor = null) => {
        const payload = cursor ? { lastKey: cursor } : {};
        
        // fetching page

        // Using POST as a fallback since GET 400 is common for missing body or wrong method in this project
        axios
          .post(`${API_CONSTANTS.GET_BOOKED_PROCEDURE_AND_SURGERY()}`, payload, {
            headers: API_CONSTANTS.headers(),
          })
          .then((res) => {
            // Extract procedures array from response
            const bookings = res.data?.response?.procedures || [];
            const responseCount = res.data?.response?.count || 0;
            const nextLastKey = res.data?.response?.lastKey || null;
            
            pageCount++;
            
            // Add bookings from this page
            allBookings = [...allBookings, ...bookings];
            // accumulated records updated
            
            // If there are more pages, fetch the next one
            if (nextLastKey) {
              fetchPage(nextLastKey);
            } else {
              // No more pages, process final results
              const recordsWithoutId = allBookings.filter(b => !b || !b.appointmentId);
              const filtered = allBookings.filter(b => b && b.appointmentId);

              // Sort by created_at descending (latest first)
              const sorted = filtered.sort((a, b) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return dateB - dateA; // Descending order (newest first)
              });

              // Check if all records were fetched (no console warnings)
              setTotalRecords(allBookings.length);
              setPagesFetched(pageCount);
              setAllSurgeries(sorted); // Store all records
              setSurgeryList(sorted.slice(0, 50)); // Show first 50
              setCurrentPage(1);
              setLoading(false);
              setIsLoading(false);
            }
          })
          .catch((error) => {
            // Try GET on any error
            // POST failed on page, retrying with GET
            
            axios.get(`${API_CONSTANTS.GET_BOOKED_PROCEDURE_AND_SURGERY()}${cursor ? `?lastKey=${encodeURIComponent(cursor)}` : ''}`, {
              headers: API_CONSTANTS.headers(),
            })
            .then(res => {
              const bookings = res.data?.response?.procedures || [];
              const responseCount = res.data?.response?.count || 0;
              const nextLastKey = res.data?.response?.lastKey || null;
              
              pageCount++;
              
              allBookings = [...allBookings, ...bookings];

              if (nextLastKey) {
                fetchPage(nextLastKey);
              } else {
                const recordsWithoutId = allBookings.filter(b => !b || !b.appointmentId);
                const filtered = allBookings.filter(b => b && b.appointmentId);

                // Sort by created_at descending (latest first)
                const sorted = filtered.sort((a, b) => {
                  const dateA = new Date(a.created_at);
                  const dateB = new Date(b.created_at);
                  return dateB - dateA; // Descending order (newest first)
                });

                setTotalRecords(allBookings.length);
                setPagesFetched(pageCount);
                setAllSurgeries(sorted); // Store all records
                setSurgeryList(sorted.slice(0, 50)); // Show first 50
                setCurrentPage(1);
                setLoading(false);
                setIsLoading(false);
              }
            })
            .catch(getErr => {
              // Failed to fetch data from DB
              setLoading(false);
              setIsLoading(false);
            });
          });
      };

      // Start fetching from first page
      fetchPage();
    },
    []
  );

  useEffect(() => {
    fetchData();
    
    return () => {
      // Cleanup to prevent state updates on unmounted component
      setLoading(false);
      setIsLoading(false);
    };
  }, [fetchData]);

  useEffect(() => {
    if (!loading && surgeryList.length > 0) {
      // Displayed in table
    } else if (!loading && surgeryList.length === 0) {
      // No booked surgeries to display
    }
  }, [surgeryList, loading, totalRecords, pagesFetched]);

  const columns = useMemo(
    () => [
      {
        Header: "Appointment ID",
        accessor: "appointmentId",
      },
      {
        Header: "Patient Name",
        accessor: "full_name",
      },
      {
        Header: "Procedure Name",
        accessor: "procedureInfo.procedure_name",
      },
      {
        Header: "Type",
        accessor: "appoint_type",
        Cell: ({ value }) => value ? value.charAt(0).toUpperCase() + value.slice(1) : "",
      },
      {
        Header: "Status",
        accessor: "booking_status",
        Cell: ({ value }) => value ? value.charAt(0).toUpperCase() + value.slice(1) : "",
      },
      {
        Header: "Date Booked",
        accessor: "created_at",
        Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : "",
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => {
          const appointmentId = row.original?.appointmentId;
          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
              <Link
                to={`/surgery/booked/${appointmentId}/overview`}
                className="btn"
                style={{
                  background: "none",
                  border: "none",
                  padding: "3px 6px",
                  color: "#009B93",
                  textDecoration: "none",
                  fontSize: "14px",
                }}
              >
                <i className="fas fa-eye"></i> View
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  const handleLoadMoreFromTable = () => {
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const newRecords = allSurgeries.slice(0, endIndex);
    
    // Load more requested for page ${nextPage}
    
    setSurgeryList(newRecords);
    setCurrentPage(nextPage);
  };

  const remainingRecords = allSurgeries.length - surgeryList.length;

  return (
    <div style={{ padding: "20px" }}>
      <div
        className="col-12"
        style={{
          background: "#fff",
          padding: "20px",
          borderRadius: "3px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <h5 style={{ fontSize: "18px", fontWeight: "600", color: "#333", marginBottom: "15px" }}>
            Booked Surgeries & Procedures
          </h5>
        </div>
        
        {loading ? (
             <div className="d-flex justify-content-center">
             <Spinner animation="border" variant="primary" />
           </div>
        ) : surgeryList.length === 0 ? (
          <div className="text-center p-5">
            <h5 style={{ color: "#8E8E8E" }}>No booked surgeries for now</h5>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "15px" }}>
              <p style={{ color: "#666", fontSize: "14px" }}>
                Showing <strong>{surgeryList.length}</strong> of <strong>{allSurgeries.length}</strong> booked surgeries
              </p>
            </div>
            <Table
              data={surgeryList}
              columns={columns}
              showFilters={false}
              fetchData={() => {}} // Dummy function - data is already loaded
              loading={false}
              setLoading={() => {}}
              manualPagination={false}
              routingLink="" // Disable default row click navigation
              lastEvaluatedKey={remainingRecords > 0 ? "more" : null} // Show button if records remain
              loadMoreList={handleLoadMoreFromTable} // Use Table's Load More button
            />
          </>
        )}
      </div>
    </div>
  );
}
